import { Auth } from 'aws-amplify';

import { FC, createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';
import { debounce } from 'lodash';

import { AUTH_STATUS } from 'src/constants';
import { TUserInfo } from 'src/types';

import { TimeoutWarningModal } from 'src/components/common/TimeoutWarningModal';
import { addEventListeners, removeEventListeners } from 'src/utils/eventListenerUtil';

const SESSION_TIMEOUT = 3600000;
const ACTIVITY_THROTTLE = 2000;
const MODAL_DECISION_TIMER = 300000;

export interface AuthContextType {
  status: AUTH_STATUS;
  isLoggedIn: boolean;
  userInfo?: TUserInfo;
  userName: string;
  userEmail: string;
  isAdmin: boolean;
  isAI: boolean | undefined;
  isDI: boolean | undefined;
  onConfirmSignUp: (userName: string, verificationCode: string) => void;
  onForgotPassword: (userName: string) => void;
  onLogIn: (email: string, password: string) => void;
  onResetPassword: (userName: string, verificationCode: string, password: string) => void;
  onSignOut: () => Promise<void>;
  onSignUp: (userName: string, password: string, email: string) => Promise<void>;
}

export const AuthContext = createContext<AuthContextType>(undefined!);

interface AuthProviderProps {
  children: Nullable<ReactNode>;
}

// Create the timer reference outside the scope of the effects and methods
// but available to the provider as a whole.  This allows us to clear and create timers
// across methods.

let idleTimer: any;
let modalTimer: any;
let initialized: boolean = false;
let activityThrottled: any;

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const [userInfo, setUserInfo] = useState<TUserInfo>();
  const [status, setLoggedInStatus] = useState(AUTH_STATUS.UNDETERMINED);
  const [isWarningModalOpen, setWarningModalOpen] = useState(false);


  /**
   * Action that'll trigger when the timeout idle period has elapsed without user input
   * this will open a signout popup on a timer to decide if they are gonna log out.
   * we must unbind the window listener to prevent pushing out the idle timer that expired
   */
  const idleTimeout = async () => {
    removeEventListeners(activityListener);
    setWarningModalOpen(true);
    clearTimeout(modalTimer);
    //console.log("Prompt Timer Started.");
    modalTimer = setTimeout(() => {
      //console.log("Prompt Timer Expired.");
      onSignOut();
    }, MODAL_DECISION_TIMER);
  };

  // Listener to manage pushing out the idle timer when the user performs some actions in the UI
  // don't dispatch the timer cleanup more than once every 10 seconds to keep the events from
  // cluttering the rest of the UI functionality.
  const activityListener = () => {
    if (!activityThrottled) {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(idleTimeout, SESSION_TIMEOUT);
      activityThrottled = setTimeout(() => {
        activityThrottled = false;
      }, ACTIVITY_THROTTLE);
      //console.log("Pushing idle timer");
    }
  };

  /**
   * Clean up the timers and event listeners, will occur on all logout events.
   */
  const stopSessionTimeout = () => {
    removeEventListeners(activityListener);
    clearTimeout(modalTimer);
    clearTimeout(idleTimer);
    initialized = false;
    //console.log("Stopping timers and event listeners");
  };

  /**
   * initSessionTimeout
   * This timer will initiate the idle logout timer, first it'll ensure no timer is currently
   * running under the `idleTimer` property, also bind the window events to clear the timer whenever
   * the user inputs an action to the screen.  Use a debouncer to ensure window event spam isn't triggering
   * a lot of actions.
   */
  const initSessionTimeout = () => {
    // Ensure the idle timer hasn't already been allocated to prevent multiple listeners
    // being bound to the window and timers as well.
    if (!initialized) {
      initialized = true;
      idleTimer = setTimeout(idleTimeout, SESSION_TIMEOUT);
      addEventListeners(activityListener);
      //console.log("Binding listeners and starting idle timer");
    }
  };


  const authenticateCurrentUser = useCallback(async () => {
    try {
      const userInfo = await Auth.currentAuthenticatedUser();
      setUserInfo(userInfo.signInUserSession.idToken.payload);
      setLoggedInStatus(AUTH_STATUS.LOGGED_IN);
      initSessionTimeout();
    } catch (error) {
      setLoggedInStatus(AUTH_STATUS.LOGGED_OUT);
      stopSessionTimeout();
    }
  }, []);

  const onSignOut = useCallback(async () => {
    await Auth.signOut();
    setLoggedInStatus(AUTH_STATUS.LOGGED_OUT);
    setWarningModalOpen(false);
    stopSessionTimeout();
  }, []);

  const onLogIn = useCallback(async (email: string, password: string) => {
    try {
      const userInfo = await Auth.signIn(email, password);
      setUserInfo(userInfo.signInUserSession.idToken.payload);
      setLoggedInStatus(AUTH_STATUS.LOGGED_IN);
      initSessionTimeout();
    } catch (error) {
      setLoggedInStatus(AUTH_STATUS.LOGGED_OUT);
      stopSessionTimeout();
      throw error;
    }
  }, []);

  const onForgotPassword = useCallback(async (userName: string) => {
    await Auth.forgotPassword(userName);
  }, []);

  const onResetPassword = useCallback(async (userName: string, verificationCode: string, password: string) => {
    await Auth.forgotPasswordSubmit(userName, verificationCode, password);
  }, []);

  const onConfirmSignUp = useCallback(async (userName: string, verificationCode: string) => {
    await Auth.confirmSignUp(userName, verificationCode);
  }, []);

  const onSignUp = useCallback(async (userName: string, password: string, email: string) => {
    await Auth.signUp(userName, password, email);
  }, []);

  useEffect(() => {
    authenticateCurrentUser();
  }, [authenticateCurrentUser]);

  const isLoggedIn = status === AUTH_STATUS.LOGGED_IN;

  const userName = userInfo?.['cognito:username'] ?? '';
  const userEmail = userInfo?.['email'] ?? '';
  const isAdmin = userInfo?.['cognito:groups']?.includes('admin') ?? false;

  const subdomainFromHost = window.location.host.split("."); 

  const aiDomain = subdomainFromHost.find((item) => item.includes('schoolcode'));
  const isAI = aiDomain?.includes('schoolcode');

  const diDomain = subdomainFromHost.find((item) => item.includes('scoresendcode'));
  const isDI = diDomain?.includes('scoresendcode');

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        status,
        userInfo,
        userName,
        userEmail,
        isAdmin,
        isAI,
        isDI,
        onConfirmSignUp,
        onForgotPassword,
        onLogIn,
        onResetPassword,
        onSignOut,
        onSignUp,
      }}>
      <div>
        <TimeoutWarningModal
          isOpen={isWarningModalOpen}
          onConfirm={() => {
            onSignOut();
          }}
          onClose={() => {
            setWarningModalOpen(false);
            stopSessionTimeout();
            initSessionTimeout();
          }}
        />
      </div>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
